/* Learn neural reveal (2026-10-07, approved prototype B "Innervation").
   Context V3 arms it for the first-item auto-open of every Learn module. The picker keeps its
   surface while its ink fades, except the chosen lecture's thumbnail: processes drawn like the
   homepage hero (home/hero-art.js) crawl out from under it and branch across the panel while the
   first item is written (Quizzes start on the first streamed byte and hold while question 1 is
   written; the other modules start when their first item is ready). When the module's surface is
   ready the thumbnail contracts into a cell body, terminals reach each line and control of the
   real surface, and each part inks in where a terminal touches it. The network then lets go, root
   first. Backgrounds never change and the last frame is the module's own surface, so the
   canvas-to-DOM handoff is invisible. Every other open keeps the routing shield fade, and so do
   reduced motion and a missing 2D canvas. */
(function (global) {
    'use strict';
    const d = global.document;
    if (!d || global.MemoraNeuralReveal) return;

    const FIRST_BYTE_EVENTS = new Set(['quickstart_first_chunk', 'first_sse_byte', 'relay_response']);
    // The cloud only covers a native open that is still on its way. An open slower than this
    // (a Tutor hand-off, a PDF load) fades the layer out rather than hold the arbor over
    // an empty card; once land() has been asked for, the landing always finishes.
    const MAX_CLOUD_S = 6;
    // The processes start crawling as the picker ink fades; terminals never leave before the
    // arbor has had a moment to spread.
    const GROW_AT_S = 0.45;
    const MIN_READY_S = 1.8;
    const MAX_TARGETS = 48;
    const STEP = 3;
    const WIDTHS = [0.5, 0.75, 1, 1.3, 1.65, 2.05];
    const LEVELS = 10;
    const FEATHER = 60;
    const ROOT_HIDDEN = 14;
    let current = null;
    let traceWrapped = false;

    const cl = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
    const ss = (a, b, x) => { const t = cl((x - a) / (b - a)); return t * t * (3 - 2 * t); };
    const ramp = (t, a, b) => cl((t - a) / (b - a));
    const lerp = (a, b, t) => a + (b - a) * t;
    const easeIO = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    function rng(seed) {
        let s = seed >>> 0;
        return () => {
            s = (s + 0x6d2b79f5) >>> 0;
            let t = s;
            t = Math.imul(t ^ (t >>> 15), t | 1);
            t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }
    // Smooth 1D value noise in -1..1, for the unsteadiness of an inked line.
    function noise1(seed) {
        const r = rng(seed);
        const v = Array.from({ length: 256 }, () => r() * 2 - 1);
        return (x) => {
            const i = Math.floor(x), f = x - i, t = f * f * (3 - 2 * f);
            return lerp(v[i & 255], v[(i + 1) & 255], t);
        };
    }

    /* color-mix() tokens compute to color(srgb r g b / a) with 0-1 channels; treating them as
       unreadable sent the reveal up to the darker body fill instead of the panel's own colour. */
    const parseColor = (s) => {
        const text = String(s || '');
        const srgb = text.match(/color\(srgb\s+([^)]*)\)/);
        if (srgb) {
            const p = srgb[1].split(/[\s/]+/).filter(Boolean).map(parseFloat);
            return [(p[0] || 0) * 255, (p[1] || 0) * 255, (p[2] || 0) * 255, p.length > 3 ? p[3] : 1];
        }
        const m = text.match(/rgba?\(([^)]*)\)/);
        if (!m) return null;
        const p = m[1].split(/[\s,/]+/).filter(Boolean).map(parseFloat);
        return [p[0] || 0, p[1] || 0, p[2] || 0, p.length > 3 ? p[3] : 1];
    };
    const lum = (c) => (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;

    /* Diagnostics own the stream timeline; the reveal listens without changing what they record. */
    function wrapTrace() {
        const trace = global.QuizGenTrace;
        if (traceWrapped || !trace || typeof trace.record !== 'function') return traceWrapped;
        const record = trace.record;
        trace.record = function (event) {
            const result = record.apply(this, arguments);
            try { if (FIRST_BYTE_EVENTS.has(String(event))) current?.firstByte(); } catch (_error) { }
            return result;
        };
        traceWrapped = true;
        return true;
    }

    /* Redraws a DOM subtree into two device-resolution canvases: low-contrast fills become the
       surface, everything else (text, borders, strong fills, icons) becomes ink. Positions come
       from the live layout, so the particles target the exact pixels the DOM will paint. */
    async function raster(root, stage, safe) {
        const { rect: sr, Wd, Hd, DPR, bgL } = stage;
        const make = () => {
            const c = d.createElement('canvas');
            c.width = Wd; c.height = Hd;
            const x = c.getContext('2d', { willReadFrequently: true });
            x.setTransform(DPR, 0, 0, DPR, 0, 0);
            return [c, x];
        };
        const [Sc, S] = make();
        const [Ic, I] = make();
        const rootBox = root.getBoundingClientRect();
        const rootArea = Math.max(1, rootBox.width * rootBox.height);
        const opacityCache = new Map();
        const opacity = (el) => {
            if (!el || el.nodeType !== 1) return 1;
            if (opacityCache.has(el)) return opacityCache.get(el);
            const value = (Number(getComputedStyle(el).opacity) || 0) * opacity(el.parentElement);
            opacityCache.set(el, value);
            return value;
        };
        const radius = (cs, w, h, z) => {
            const raw = String(cs.borderTopLeftRadius || '0');
            const v = parseFloat(raw) || 0;
            return Math.min(raw.endsWith('%') ? v / 100 * Math.min(w, h) : v * z, w / 2, h / 2);
        };
        /* Overflow clipping inside the sampled subtree (sliding views, scrollers) hides content
           the DOM never paints; each element keeps the intersection of its clipping ancestors. */
        const clipCache = new Map();
        const clipOf = (el) => {
            if (!el || el === root.parentElement) return { l: -1e6, t: -1e6, r: 1e6, b: 1e6 };
            if (clipCache.has(el)) return clipCache.get(el);
            let clip = clipOf(el.parentElement);
            const parent = el.parentElement;
            if (parent && root.contains(parent)) {
                const ps = getComputedStyle(parent);
                if (ps.overflowX !== 'visible' || ps.overflowY !== 'visible') {
                    const pr = parent.getBoundingClientRect();
                    clip = { l: Math.max(clip.l, pr.left), t: Math.max(clip.t, pr.top), r: Math.min(clip.r, pr.right), b: Math.min(clip.b, pr.bottom) };
                }
            }
            clipCache.set(el, clip);
            return clip;
        };
        const clipped = (el, b) => {
            const c = clipOf(el);
            return b.right <= c.l + 0.5 || b.left >= c.r - 0.5 || b.bottom <= c.t + 0.5 || b.top >= c.b - 0.5;
        };
        /* Transition chrome (the routing shield, the picker once it has stepped aside, these
           layers) is never part of the destination. */
        const skip = root.id === 'memora-rail-context'
            ? '#memora-live-route-transition, .memora-neural-reveal-layer'
            : '#memora-live-route-transition, .memora-neural-reveal-layer, #memora-rail-context';
        const skipped = (el) => {
            for (let node = el; node && node !== root; node = node.parentElement) if (node.matches(skip)) return true;
            return false;
        };
        const pictures = [];
        for (const el of [root, ...root.querySelectorAll('*')]) {
            const tag = el.tagName.toLowerCase();
            if (tag !== 'svg' && el.closest('svg')) continue;
            if (skipped(el)) continue;
            const cs = getComputedStyle(el);
            if (cs.display === 'none' || cs.visibility === 'hidden') continue;
            const alpha = opacity(el);
            if (alpha < 0.01) continue;
            const b = el.getBoundingClientRect();
            const x = b.left - sr.left, y = b.top - sr.top, w = b.width, h = b.height;
            if (w < 0.5 || h < 0.5 || x > sr.width || y > sr.height || x + w < 0 || y + h < 0 || clipped(el, b)) continue;
            if (tag === 'svg' || tag === 'img' || tag === 'canvas') {
                if (!safe) pictures.push({ el, tag, x, y, w, h, alpha });
                continue;
            }
            const z = Number(el.currentCSSZoom) || 1;
            const rad = radius(cs, w, h, z);
            S.globalAlpha = alpha; I.globalAlpha = alpha;
            /* Panels and cards are surface: quiet fills covering at least a fifth of the sampled
               root. They keep their shadows and stay put through the reveal; every smaller or
               stronger fill is ink. */
            const textClip = /text/.test(cs.backgroundClip || '') || /text/.test(cs.webkitBackgroundClip || '');
            const bg = textClip ? null : parseColor(cs.backgroundColor);
            const grad = textClip ? null : gradientOf(cs.backgroundImage);
            const quiet = (c) => c[3] * Math.abs(lum(c) - bgL) <= 0.05;
            const big = w * h >= rootArea * 0.2;
            const filled = (bg && bg[3] > 0) || !!grad;
            if (big && filled && cs.boxShadow && cs.boxShadow !== 'none') paintShadows(S, cs.boxShadow, x, y, w, h, rad, z, DPR);
            if (bg && bg[3] > 0) {
                const target = big && quiet(bg) ? S : I;
                target.fillStyle = cs.backgroundColor; target.beginPath(); target.roundRect(x, y, w, h, rad); target.fill();
            }
            if (grad) paintGradient(big && quiet(grad.mean) ? S : I, grad, x, y, w, h, rad);
            const sides = ['Top', 'Right', 'Bottom', 'Left'].map((side) => {
                const width = cs[`border${side}Style`] === 'none' ? 0 : (parseFloat(cs[`border${side}Width`]) || 0) * z;
                const color = parseColor(cs[`border${side}Color`]);
                return { width: color && color[3] > 0 ? width : 0, color: cs[`border${side}Color`] };
            });
            const uniform = sides.every((side) => side.width === sides[0].width && side.color === sides[0].color);
            if (uniform && sides[0].width > 0) {
                const bw = sides[0].width;
                I.strokeStyle = sides[0].color; I.lineWidth = bw;
                I.beginPath(); I.roundRect(x + bw / 2, y + bw / 2, w - bw, h - bw, Math.max(0, rad - bw / 2)); I.stroke();
            } else if (!uniform) {
                const [top, right, bottom, left] = sides;
                if (top.width) { I.fillStyle = top.color; I.fillRect(x, y, w, top.width); }
                if (bottom.width) { I.fillStyle = bottom.color; I.fillRect(x, y + h - bottom.width, w, bottom.width); }
                if (left.width) { I.fillStyle = left.color; I.fillRect(x, y, left.width, h); }
                if (right.width) { I.fillStyle = right.color; I.fillRect(x + w - right.width, y, right.width, h); }
            }
        }
        S.globalAlpha = 1;
        const walker = d.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const range = d.createRange();
        let node;
        while ((node = walker.nextNode())) {
            const parent = node.parentElement;
            if (!parent || parent.closest('svg, select, option, script, style, textarea') || skipped(parent)) continue;
            const cs = getComputedStyle(parent);
            if (cs.visibility === 'hidden') continue;
            const alpha = opacity(parent);
            if (alpha < 0.01) continue;
            let fill = cs.webkitTextFillColor || cs.color;
            const fillColor = parseColor(fill);
            if (fillColor && fillColor[3] === 0) {
                const gradient = (cs.backgroundImage || '').match(/rgba?\([^)]*\)/);
                fill = gradient ? gradient[0] : cs.color;
            }
            const z = Number(parent.currentCSSZoom) || 1;
            I.globalAlpha = alpha;
            I.font = `${cs.fontStyle} ${cs.fontWeight} ${(parseFloat(cs.fontSize) || 14) * z}px ${cs.fontFamily}`;
            I.fillStyle = fill;
            const s = node.textContent;
            for (let i = 0; i < s.length; i += 1) {
                let ch = s[i];
                if (!ch.trim()) continue;
                const code = s.charCodeAt(i);
                const span = code >= 0xd800 && code <= 0xdbff ? 2 : 1;
                if (span === 2) ch = s.slice(i, i + 2);
                range.setStart(node, i); range.setEnd(node, i + span);
                i += span - 1;
                const b = range.getClientRects()[0];
                if (!b || b.width < 0.1 || clipped(parent, b)) continue;
                if (cs.textTransform === 'uppercase') ch = ch.toUpperCase();
                const m = I.measureText(ch), a = m.fontBoundingBoxAscent, dsc = m.fontBoundingBoxDescent;
                I.fillText(ch, b.left - sr.left, b.top - sr.top + (b.height - a - dsc) / 2 + a);
            }
        }
        for (const p of pictures) {
            const image = p.tag === 'svg' ? await svgImage(p.el, p.w, p.h) : p.el;
            if (!image || (p.tag === 'img' && !(p.el.complete && p.el.naturalWidth)) || (p.tag === 'canvas' && !(p.el.width && p.el.height))) continue;
            I.globalAlpha = p.alpha;
            try { I.drawImage(image, p.x, p.y, p.w, p.h); } catch (_error) { }
        }
        I.globalAlpha = 1;
        return { S: Sc, I: Ic };
    }

    function svgImage(svg, w, h) {
        const clone = svg.cloneNode(true);
        const from = [svg, ...svg.querySelectorAll('*')];
        const to = [clone, ...clone.querySelectorAll('*')];
        const props = ['fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width', 'stroke-opacity', 'stroke-linecap',
            'stroke-linejoin', 'stroke-dasharray', 'opacity', 'color', 'visibility', 'display', 'font-size', 'font-family', 'font-weight'];
        from.forEach((node, i) => {
            const cs = getComputedStyle(node);
            to[i]?.setAttribute('style', props.map((p) => `${p}:${cs.getPropertyValue(p)}`).join(';'));
        });
        clone.setAttribute('width', String(w));
        clone.setAttribute('height', String(h));
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const image = new Image();
        image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
        return image.decode().then(() => image, () => null);
    }

    /* CSS gradients as canvas gradients. Computed styles give rgb() and color(srgb) stops; layers
       paint bottom first, so a panel's own fill and the glows over it come out as on screen. */
    const splitTop = (s) => {
        const out = [];
        let depth = 0, from = 0;
        for (let i = 0; i < s.length; i += 1) {
            if (s[i] === '(') depth += 1;
            else if (s[i] === ')') depth -= 1;
            else if (s[i] === ',' && !depth) { out.push(s.slice(from, i).trim()); from = i + 1; }
        }
        out.push(s.slice(from).trim());
        return out.filter(Boolean);
    };
    const COLOR_STOP = /^(rgba?\([^)]*\)|color\(srgb[^)]*\)|transparent)\s*(.*)$/i;
    function gradientOf(image) {
        if (!image || image === 'none' || !image.includes('gradient') || image.includes('url(')) return null;
        const layers = [];
        for (const layer of splitTop(image).reverse()) {
            const m = layer.match(/^(repeating-)?(linear|radial)-gradient\(([\s\S]*)\)$/);
            if (!m || m[1]) return null;
            const args = splitTop(m[3]);
            const head = COLOR_STOP.test(args[0]) ? '' : args.shift();
            const stops = args.map((arg) => {
                const c = arg.match(COLOR_STOP);
                const col = c && (c[1].toLowerCase() === 'transparent' ? [0, 0, 0, 0] : parseColor(c[1]));
                return col ? { col, pos: /%/.test(c[2]) ? parseFloat(c[2]) / 100 : null } : null;
            });
            if (stops.length < 2 || stops.some((s) => !s)) return null;
            stops[0].pos ??= 0;
            stops[stops.length - 1].pos ??= 1;
            for (let i = 1; i < stops.length - 1; i += 1) {
                if (stops[i].pos != null) continue;
                let j = i;
                while (stops[j].pos == null) j += 1;
                const a = stops[i - 1].pos, b = stops[j].pos;
                for (let k = i; k < j; k += 1) stops[k].pos = a + (b - a) * (k - i + 1) / (j - i + 1);
            }
            layers.push({ kind: m[2], head, stops });
        }
        let r = 0, g = 0, b = 0, a = 0, n = 0;
        for (const layer of layers) for (const s of layer.stops) { r += s.col[0] * s.col[3]; g += s.col[1] * s.col[3]; b += s.col[2] * s.col[3]; a += s.col[3]; n += 1; }
        return { layers, mean: a > 0 ? [r / a, g / a, b / a, a / n] : [0, 0, 0, 0] };
    }
    function paintGradient(T, grad, x, y, w, h, rad) {
        for (const layer of grad.layers) {
            T.beginPath(); T.roundRect(x, y, w, h, rad);
            let paint, cx = 0, cy = 0, squash = 1;
            if (layer.kind === 'linear') {
                const head = layer.head.trim(), deg = head.match(/(-?[\d.]+)deg/);
                let angle = 180;
                if (deg) angle = parseFloat(deg[1]);
                else if (head.startsWith('to ')) {
                    const vx = /right/.test(head) ? 1 : /left/.test(head) ? -1 : 0, vy = /bottom/.test(head) ? 1 : /top/.test(head) ? -1 : 0;
                    angle = Math.atan2(vx * h, -vy * w) * 180 / Math.PI;
                }
                const a = angle * Math.PI / 180, dx = Math.sin(a), dy = -Math.cos(a), len = Math.abs(w * dx) + Math.abs(h * dy);
                const mx = x + w / 2, my = y + h / 2;
                paint = T.createLinearGradient(mx - dx * len / 2, my - dy * len / 2, mx + dx * len / 2, my + dy * len / 2);
            } else {
                const at = layer.head.match(/at\s+(\S+)(?:\s+(\S+))?/);
                const pos = (v, size) => {
                    if (!v) return size / 2;
                    if (v.endsWith('%')) return parseFloat(v) / 100 * size;
                    if (v.endsWith('px')) return parseFloat(v);
                    return { left: 0, top: 0, right: size, bottom: size }[v] ?? size / 2;
                };
                cx = x + pos(at?.[1], w); cy = y + pos(at?.[2], h);
                const fx = Math.max(cx - x, x + w - cx), fy = Math.max(cy - y, y + h - cy);
                const circle = /circle/.test(layer.head);
                const rx = Math.max(1e-3, circle ? Math.hypot(fx, fy) : fx * Math.SQRT2), ry = circle ? rx : fy * Math.SQRT2;
                squash = ry / rx;
                paint = T.createRadialGradient(cx, cy, 0, cx, cy, rx);
            }
            layer.stops.forEach((s) => paint.addColorStop(cl(s.pos), `rgba(${s.col[0]},${s.col[1]},${s.col[2]},${s.col[3]})`));
            T.fillStyle = paint;
            // The path is fixed when built; only the radial paint is squashed into its ellipse.
            if (squash !== 1) { T.save(); T.translate(cx, cy); T.scale(1, squash); T.translate(-cx, -cy); T.fill(); T.restore(); }
            else T.fill();
        }
    }
    // Outer box shadows, drawn from a shape parked far off canvas so only the shadow lands.
    function paintShadows(T, value, x, y, w, h, rad, z, DPR) {
        const FAR = 100000;
        for (const part of splitTop(value).reverse()) {
            if (/inset/.test(part)) continue;
            const col = parseColor((part.match(/rgba?\([^)]*\)|color\(srgb[^)]*\)/) || ['rgba(0,0,0,.4)'])[0]);
            if (!col || col[3] <= 0) continue;
            const v = (part.replace(/rgba?\([^)]*\)|color\(srgb[^)]*\)/g, '').match(/-?[\d.]+px/g) || []).map((n) => parseFloat(n) * z);
            const spread = v[3] || 0;
            T.save();
            T.shadowColor = `rgba(${col[0]},${col[1]},${col[2]},${col[3]})`;
            T.shadowOffsetX = ((v[0] || 0) + FAR) * DPR; T.shadowOffsetY = (v[1] || 0) * DPR; T.shadowBlur = (v[2] || 0) * DPR;
            T.fillStyle = '#000';
            T.beginPath(); T.roundRect(x - spread - FAR, y - spread, w + 2 * spread, h + 2 * spread, Math.max(0, rad + spread)); T.fill();
            T.restore();
        }
    }

    async function pixels(root, stage) {
        let out = await raster(root, stage, false);
        try {
            out.ink = out.I.getContext('2d').getImageData(0, 0, stage.Wd, stage.Hd).data;
        } catch (_tainted) {
            out = await raster(root, stage, true);
            out.ink = out.I.getContext('2d').getImageData(0, 0, stage.Wd, stage.Hd).data;
        }
        return out;
    }

    function backgroundOf(el) {
        for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
            const cs = getComputedStyle(node);
            const color = parseColor(cs.backgroundColor);
            if (color && color[3] > 0.5) return color;
            const gradient = (cs.backgroundImage || '').match(/rgba?\([^)]*\)|color\(srgb[^)]*\)/);
            if (gradient) return parseColor(gradient[0]);
        }
        return [25, 26, 24, 1];
    }

    function accentColor() {
        const probe = d.createElement('span');
        probe.style.cssText = 'position:absolute;width:0;height:0;color:var(--g1,#d97757)';
        d.body.appendChild(probe);
        const color = parseColor(getComputedStyle(probe).color) || [217, 119, 87, 1];
        probe.remove();
        return color;
    }

    /* The network, ported from the homepage hero (home/hero-art.js): a process is a polyline with
       a width and a path distance from its root at every point, and a reveal front stops partway
       along it. Every visible segment goes into one Path2D per width class and alpha level, so the
       whole arbor is a few dozen strokes however many segments it has. */
    const widthClass = (w) => { let k = 0; while (k < WIDTHS.length - 1 && w > (WIDTHS[k] + WIDTHS[k + 1]) / 2) k += 1; return k; };
    // Processes start a little under the thumbnail and fade up only once they are past its edge.
    const fromRoot = (dist, root) => (root == null ? 1 : ss(ROOT_HIDDEN, ROOT_HIDDEN + 30, dist - root));
    const newPoly = (alpha, root) => ({ x: [], y: [], w: [], d: [], sp: [], alpha, root: root == null ? null : root });

    function resample(raw, step) {
        const out = [raw[0].slice()];
        let px = raw[0][0], py = raw[0][1], acc = 0;
        for (let i = 1; i < raw.length; i += 1) {
            const qx = raw[i][0], qy = raw[i][1];
            let seg = Math.hypot(qx - px, qy - py);
            while (seg > 0 && acc + seg >= step) {
                const k = (step - acc) / seg;
                px += (qx - px) * k; py += (qy - py) * k;
                out.push([px, py]);
                seg = Math.hypot(qx - px, qy - py);
                acc = 0;
            }
            acc += seg; px = qx; py = qy;
        }
        const end = raw[raw.length - 1], tail = out[out.length - 1];
        if (Math.hypot(end[0] - tail[0], end[1] - tail[1]) > step * 0.35) out.push(end.slice());
        else out[out.length - 1] = end.slice();
        return out;
    }

    function spine(P, r, x, y, a, dist, alpha) {
        const sa = a + (r() < 0.5 ? -1 : 1) * (1.1 + r() * 0.6), sl = 1.3 + r() * 2.1;
        P.sp.push(x, y, x + Math.cos(sa) * sl, y + Math.sin(sa) * sl, dist, alpha);
    }

    // A process from R to T: a bowed cubic with the slight unsteadiness of an inked line.
    function trunk(net, r, R, T, o) {
        const dx = T[0] - R[0], dy = T[1] - R[1], L0 = Math.hypot(dx, dy);
        const c1 = [R[0] + dx * 0.34, R[1] + dy * 0.1 + o.bend * L0];
        const c2 = [T[0] - dx * 0.42, T[1] - dy * 0.15 - o.bend * 0.35 * L0];
        const raw = [];
        for (let i = 0; i <= 240; i += 1) {
            const t = i / 240, m = 1 - t, k0 = m * m * m, k1 = 3 * m * m * t, k2 = 3 * m * t * t, k3 = t * t * t;
            raw.push([k0 * R[0] + k1 * c1[0] + k2 * c2[0] + k3 * T[0], k0 * R[1] + k1 * c1[1] + k2 * c2[1] + k3 * T[1]]);
        }
        const pts = resample(raw, STEP), n = pts.length, nA = noise1(o.seed), nB = noise1(o.seed + 5);
        const P = newPoly(o.alpha, o.root === undefined ? (o.lag || 0) : o.root);
        const wobble = o.wobble == null ? 8 : o.wobble;
        let dist = o.lag || 0;
        for (let i = 0; i < n; i += 1) {
            const u = i / (n - 1), fwd = pts[Math.min(n - 1, i + 1)], back = pts[Math.max(0, i - 1)];
            const tx = fwd[0] - back[0], ty = fwd[1] - back[1], tl = Math.hypot(tx, ty) || 1;
            const off = (nA((i * STEP) / 64) * wobble + nB((i * STEP) / 15) * 1.4) * Math.pow(Math.sin(Math.PI * u), 0.7);
            const x = pts[i][0] - (ty / tl) * off, y = pts[i][1] + (tx / tl) * off;
            if (i) dist += Math.hypot(x - P.x[i - 1], y - P.y[i - 1]);
            P.x.push(x); P.y.push(y); P.d.push(dist); P.w.push(lerp(o.w0, o.w1, Math.pow(u, 0.75)));
        }
        for (let i = 2; i < P.x.length; i += 1) {
            if (P.w[i] < 1.35 && r() < 0.14) spine(P, r, P.x[i], P.y[i], Math.atan2(P.y[i] - P.y[i - 1], P.x[i] - P.x[i - 1]), P.d[i], o.alpha * fromRoot(P.d[i], P.root));
        }
        net.push(P);
        return P;
    }

    // Branches as on the plate: a wandering, tapering stroke with spines, forking at its end.
    function arbor(net, r, x, y, a, len, wd, dist, depth, maxDepth, alpha) {
        const n = Math.max(3, Math.round(len / STEP)), P = newPoly(alpha, null);
        P.x.push(x); P.y.push(y); P.w.push(wd); P.d.push(dist);
        for (let i = 0; i < n; i += 1) {
            a += (r() - 0.5) * 0.26; x += Math.cos(a) * STEP; y += Math.sin(a) * STEP; dist += STEP;
            const lw = Math.max(0.45, wd * (1 - (i / n) * 0.5));
            P.x.push(x); P.y.push(y); P.w.push(lw); P.d.push(dist);
            if (lw < 1.3 && r() < 0.2) spine(P, r, x, y, a, dist, alpha);
        }
        net.push(P);
        if (depth < maxDepth) {
            const kids = r() < 0.28 ? 3 : 2;
            for (let k = 0; k < kids; k += 1) {
                arbor(net, r, x, y, a + (k - (kids - 1) / 2) * (0.42 + r() * 0.34), len * (0.55 + r() * 0.2), wd * 0.62, dist, depth + 1, maxDepth, alpha * 0.92);
            }
        }
    }

    function finalize(net) {
        net.forEach((P) => {
            if (P.wc) return;
            const n = P.x.length;
            P.wc = Uint8Array.from(P.w.map((w, i) => widthClass(i < n - 1 ? (w + P.w[i + 1]) / 2 : w)));
            P.k = Float32Array.from(P.d.map((dist, i) => P.alpha * fromRoot(i < n - 1 ? (dist + P.d[i + 1]) / 2 : dist, P.root)));
            P.x = Float32Array.from(P.x); P.y = Float32Array.from(P.y); P.d = Float32Array.from(P.d);
            P.dEnd = P.d[n - 1];
        });
        return net;
    }

    /* The arbor grows out of the thumbnail toward points fanned across the stage, with side
       branches all along and a small tuft where each process stops. Larger panels get more
       processes and longer branches, so the density matches the approved prototype. */
    const FAN = [[0.63, 0.56], [0.92, 0.11], [0.93, 0.62], [0.82, 0.93], [0.37, 0.94], [0.08, 0.79],
        [0.49, 0.08], [0.07, 0.1], [0.7, 0.32], [0.25, 0.55], [0.55, 0.75], [0.97, 0.4]];
    const FAN_W = [2.1, 1.7, 1.6, 1.8, 1.4, 1.1, 1.2, 0.9, 1.3, 1.2, 1, 0.9];
    const FAN_A = [0.92, 0.7, 0.66, 0.74, 0.55, 0.4, 0.45, 0.35, 0.6, 0.5, 0.45, 0.4];
    const FAN_LAG = [0, 10, 20, 6, 30, 50, 40, 60, 15, 25, 35, 45];
    function buildTree(stage, boxes) {
        const { W, H } = stage, r = rng(1207), net = [];
        const scale = cl(Math.sqrt((W * H) / (600 * 380)), 1, 1.7);
        // Several selected lectures share the fan, so each sends out a few processes of its own.
        const count = Math.round(cl(8 * scale + 2 * (boxes.length - 1), 8, FAN.length));
        const trunks = FAN.slice(0, count).map(([fx, fy], k) => {
            const box = boxes[k % boxes.length], cx = box.x + box.w / 2, cy = box.y + box.h / 2;
            const T = [fx * W, fy * H], ang = Math.atan2(T[1] - cy, T[0] - cx);
            const ux = Math.cos(ang), uy = Math.sin(ang);
            const reach = Math.min(Math.abs(ux) > 1e-3 ? box.w / 2 / Math.abs(ux) : 1e9, Math.abs(uy) > 1e-3 ? box.h / 2 / Math.abs(uy) : 1e9);
            const lift = Math.max(0, reach - ROOT_HIDDEN);
            return trunk(net, r, [cx + ux * lift, cy + uy * lift], T, { bend: (r() - 0.5) * 0.2, w0: FAN_W[k], w1: 0.55, alpha: FAN_A[k], seed: 1207 + k * 7, lag: FAN_LAG[k] });
        });
        trunks.forEach((t, k) => {
            const n = t.x.length;
            if (k && n > 4) {
                const a = Math.atan2(t.y[n - 1] - t.y[n - 4], t.x[n - 1] - t.x[n - 4]);
                arbor(net, r, t.x[n - 1], t.y[n - 1], a - 0.45, (26 + r() * 30) * scale, 0.75, t.d[n - 1], 0, 1, FAN_A[k]);
                arbor(net, r, t.x[n - 1], t.y[n - 1], a + 0.45, (26 + r() * 30) * scale, 0.75, t.d[n - 1], 0, 1, FAN_A[k]);
            }
            let next = 30 + r() * 50, turn = r() < 0.5 ? 1 : -1;
            for (let i = 2; i < n - 3; i += 1) {
                const u = i / (n - 1);
                if (u > (k ? 0.9 : 0.8)) break;
                if (t.d[i] - t.d[0] < next) continue;
                next = t.d[i] - t.d[0] + (50 + r() * 70) / Math.sqrt(scale);
                turn = -turn;
                const a0 = Math.atan2(t.y[i + 1] - t.y[i - 1], t.x[i + 1] - t.x[i - 1]);
                const len = (44 + r() * 100) * (1 - 0.4 * u) * (k ? 0.78 : 1) * scale;
                arbor(net, r, t.x[i], t.y[i], a0 + turn * (0.42 + r() * 0.62), len, Math.max(0.55, t.w[i] * 0.6), t.d[i], 0, k ? 1 : 2, t.alpha * 0.86);
            }
        });
        finalize(net);
        return { net, trunks, Dmax: Math.max(...net.map((P) => P.dEnd)) };
    }

    function drawNet(ctx, net, frontOf, alphaAt, rgb) {
        const stride = LEVELS + 1, paths = [];
        const path = (k) => paths[k] || (paths[k] = new Path2D());
        for (const P of net) {
            const D = frontOf(P);
            if (!(D > P.d[0])) continue;
            const n = P.x.length;
            let prev = -1;
            for (let j = 0; j < n - 1; j += 1) {
                const d0 = P.d[j];
                if (d0 >= D) break;
                const x0 = P.x[j], y0 = P.y[j];
                let x1 = P.x[j + 1], y1 = P.y[j + 1], d1 = P.d[j + 1];
                if (d1 > D) { const k = (D - d0) / (d1 - d0); x1 = x0 + (x1 - x0) * k; y1 = y0 + (y1 - y0) * k; d1 = D; }
                const dm = (d0 + d1) / 2, kk = P.k[j];
                const q = kk < 0.05 ? 0 : Math.round(kk * ss(0, 1, (D - dm) / FEATHER) * alphaAt(dm) * LEVELS);
                if (q < 1) { prev = -1; continue; }
                const key = P.wc[j] * stride + q, p2 = path(key);
                if (key !== prev) p2.moveTo(x0, y0);
                p2.lineTo(x1, y1);
                prev = key;
            }
            const S = P.sp;
            for (let i = 0; i < S.length; i += 6) {
                if (S[i + 4] >= D) continue;
                const q = Math.round(S[i + 5] * 0.85 * ss(0, 1, (D - S[i + 4]) / FEATHER) * alphaAt(S[i + 4]) * LEVELS);
                if (q < 1) continue;
                const p2 = path(q);
                p2.moveTo(S[i], S[i + 1]); p2.lineTo(S[i + 2], S[i + 3]);
            }
        }
        ctx.lineCap = 'butt'; ctx.lineJoin = 'round'; ctx.strokeStyle = `rgb(${rgb})`;
        for (let key = 0; key < paths.length; key += 1) {
            if (!paths[key]) continue;
            ctx.globalAlpha = (key % stride) / LEVELS;
            ctx.lineWidth = WIDTHS[Math.floor(key / stride)];
            ctx.stroke(paths[key]);
        }
        ctx.globalAlpha = 1;
    }

    // A pulse running out along a process while the stream is still writing the first item.
    function drawSignal(ctx, P, s, rgb) {
        const tail = 56;
        ctx.lineCap = 'round'; ctx.lineWidth = 1.6;
        for (let j = 0; j < P.x.length - 1; j += 1) {
            const dist = P.d[j];
            if (dist > s) break;
            if (dist < s - tail) continue;
            const a = 0.8 * (1 - (s - dist) / tail) * fromRoot(dist, P.root);
            if (a < 0.02) continue;
            ctx.strokeStyle = `rgba(${rgb},${a.toFixed(3)})`;
            ctx.beginPath(); ctx.moveTo(P.x[j], P.y[j]); ctx.lineTo(P.x[j + 1], P.y[j + 1]); ctx.stroke();
        }
    }

    // Where a terminal touches the surface: a faint accent point and a ring that opens and fades.
    // Kept deliberately quiet so the contacts never read as bullets down the left edge.
    function drawContact(ctx, x, y, t, rgb) {
        const a = ss(0, 0.1, t) * (1 - ss(0.45, 1, t));
        if (a <= 0.01) return;
        ctx.lineWidth = 0.8;
        ctx.strokeStyle = `rgba(${rgb},${(0.16 * a * (1 - ramp(t, 0.15, 0.9))).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(x, y, 2 + 6 * easeOut(ramp(t, 0, 0.9)), 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = `rgba(${rgb},${(0.3 * a).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(x, y, 1.4, 0, Math.PI * 2); ctx.fill();
    }

    function rrLoop(x, y, w, h, rad) {
        const r = Math.max(0, Math.min(rad, w / 2, h / 2)), pts = [];
        const corner = (cx, cy, a0) => { for (let i = 0; i <= 8; i += 1) { const a = a0 + (i / 8) * (Math.PI / 2); pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]); } };
        corner(x + w - r, y + r, -Math.PI / 2); corner(x + w - r, y + h - r, 0); corner(x + r, y + h - r, Math.PI / 2); corner(x + r, y + r, Math.PI);
        pts.push(pts[0].slice());
        const s = [0];
        for (let i = 1; i < pts.length; i += 1) s.push(s[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
        // The trace starts where the terminal lands, the middle of the left side.
        let start = 0, best = 1e9;
        pts.forEach((p, i) => { const dd = Math.hypot(p[0] - x, p[1] - (y + h / 2)); if (dd < best) { best = dd; start = s[i]; } });
        return { pts, s, total: s[s.length - 1], start };
    }
    function loopAt(L, s) {
        s = ((s % L.total) + L.total) % L.total;
        let i = 1;
        while (i < L.s.length - 1 && L.s[i] < s) i += 1;
        const k = (s - L.s[i - 1]) / ((L.s[i] - L.s[i - 1]) || 1);
        return [lerp(L.pts[i - 1][0], L.pts[i][0], k), lerp(L.pts[i - 1][1], L.pts[i][1], k)];
    }
    function strokeSpan(ctx, L, a, b) {
        if (b <= a) return;
        ctx.beginPath();
        const p0 = loopAt(L, a);
        ctx.moveTo(p0[0], p0[1]);
        for (let s = a + 3; s < b; s += 3) { const p = loopAt(L, s); ctx.lineTo(p[0], p[1]); }
        const p1 = loopAt(L, b);
        ctx.lineTo(p1[0], p1[1]);
        ctx.stroke();
    }

    /* The parts of the module's surface a terminal reaches: boxes (bordered or filled controls,
       media) and the lines of text outside them, in reading order. A box's own text inks with it.
       Long surfaces are grouped so the terminals still leave within about a second. */
    function collectTargets(root, stage) {
        const sr = stage.rect, rr = root.getBoundingClientRect();
        const bounds = { l: Math.max(rr.left, sr.left), t: Math.max(rr.top, sr.top), r: Math.min(rr.right, sr.right), b: Math.min(rr.bottom, sr.bottom) };
        const area = Math.max(1, (bounds.r - bounds.l) * (bounds.b - bounds.t));
        const clip = (b) => {
            const l = Math.max(b.left, bounds.l), t = Math.max(b.top, bounds.t), r = Math.min(b.right, bounds.r), btm = Math.min(b.bottom, bounds.b);
            return r - l > 2 && btm - t > 2 ? { x: l - sr.left, y: t - sr.top, w: r - l, h: btm - t } : null;
        };
        const boxes = [];
        for (const el of root.querySelectorAll('*')) {
            const tag = el.tagName.toLowerCase();
            if (tag !== 'svg' && el.closest('svg')) continue;
            if (el.closest('.memora-neural-reveal-layer, #memora-live-route-transition')) continue;
            const cs = getComputedStyle(el);
            if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) < 0.05) continue;
            const b = el.getBoundingClientRect();
            if (b.width < 12 || b.height < 10 || b.width * b.height > area * 0.25) continue;
            const media = ['img', 'svg', 'canvas', 'video'].includes(tag);
            const bg = parseColor(cs.backgroundColor);
            const border = ['Top', 'Right', 'Bottom', 'Left'].some((side) => cs[`border${side}Style`] !== 'none'
                && parseFloat(cs[`border${side}Width`]) > 0 && (parseColor(cs[`border${side}Color`])?.[3] || 0) > 0.05);
            if (!media && !border && !(bg && bg[3] > 0.04) && !['button', 'input', 'textarea', 'select'].includes(tag)) continue;
            const box = clip(b);
            if (box) boxes.push(Object.assign(box, { kind: 'box', rad: Math.min(parseFloat(cs.borderTopLeftRadius) || 0, 24) }));
        }
        const inside = (a, b) => a !== b && a.x >= b.x - 1 && a.y >= b.y - 1 && a.x + a.w <= b.x + b.w + 1 && a.y + a.h <= b.y + b.h + 1;
        const outer = boxes.filter((a) => !boxes.some((b) => inside(a, b)));
        const lines = [];
        const walker = d.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const range = d.createRange();
        let node;
        while ((node = walker.nextNode())) {
            if (!node.textContent.trim()) continue;
            const parent = node.parentElement;
            if (!parent || parent.closest('svg, script, style, select, option, .memora-neural-reveal-layer')) continue;
            const cs = getComputedStyle(parent);
            if (cs.visibility === 'hidden' || cs.display === 'none') continue;
            range.selectNodeContents(node);
            for (const b of range.getClientRects()) {
                const line = b.width > 2 && b.height > 4 ? clip(b) : null;
                if (line && !outer.some((o) => inside(line, o))) lines.push(Object.assign(line, { kind: 'text' }));
            }
        }
        lines.sort((a, b) => a.y - b.y || a.x - b.x);
        const merged = [];
        for (const line of lines) {
            const last = merged[merged.length - 1];
            if (last && Math.abs(last.y - line.y) < 3 && line.x - (last.x + last.w) < 12 && line.x >= last.x) {
                const r = Math.max(last.x + last.w, line.x + line.w), b = Math.max(last.y + last.h, line.y + line.h);
                last.y = Math.min(last.y, line.y); last.w = r - last.x; last.h = b - last.y;
            } else merged.push(line);
        }
        let targets = [...outer, ...merged].sort((a, b) => a.y - b.y || a.x - b.x);
        if (targets.length > MAX_TARGETS) {
            const size = Math.ceil(targets.length / MAX_TARGETS), grouped = [];
            for (let i = 0; i < targets.length; i += size) {
                const part = targets.slice(i, i + size);
                const l = Math.min(...part.map((p) => p.x)), t = Math.min(...part.map((p) => p.y));
                const r = Math.max(...part.map((p) => p.x + p.w)), b = Math.max(...part.map((p) => p.y + p.h));
                grouped.push({ kind: 'text', x: l, y: t, w: r - l, h: b - t });
            }
            targets = grouped;
        }
        return targets;
    }

    function createController(options) {
        const work = d.getElementById('work-area');
        const ctl = {
            state: 'armed', T: 1e4, tau: 0, t0: 0, raf: 0, landResolve: null, wantTimer: 0,
            firstByte() { if (ctl.state === 'armed' && options.firstByte) ctl.starting = start(); },
            commit() {
                ctl.committed = true;
                if (ctl.state === 'armed') ctl.starting = start();
                return ctl.starting;
            },
            land, abort
        };
        let canvas = null, ctx = null, stage = null, pal = null, pick = null, thumbs = [], tree = null, landing = null, net = null;

        // A selected row's thumbnail, when the row is actually on screen and not scrolled away.
        const sourceOf = (row) => {
            const sr = stage.rect, rb = row.getBoundingClientRect();
            if (rb.width < 4 || rb.bottom <= sr.top || rb.top >= sr.bottom) return null;
            const source = row.querySelector('.memora-source-thumbnail') || row.querySelector('img, canvas, svg');
            const sb = source?.getBoundingClientRect();
            const box = sb && sb.width > 4 && sb.height > 4
                ? { x: sb.left - sr.left, y: sb.top - sr.top, w: sb.width, h: sb.height }
                : { x: rb.left - sr.left + 12, y: rb.top - sr.top + rb.height / 2 - 18, w: 54, h: 36 };
            const hit = d.elementFromPoint(sr.left + box.x + box.w / 2, sr.top + box.y + box.h / 2);
            if (!hit || !row.contains(hit)) return null;
            const rad = source ? Math.min(parseFloat(getComputedStyle(source).borderTopLeftRadius) || 0, box.w / 2, box.h / 2) : 6;
            return { box, rad, art: null };
        };

        const wantPoll = () => {
            if (ctl.state === 'armed' || ctl.state === 'cloud') {
                if (!ctl.committed && !options.isWanted?.()) { abort(); return; }
                if (ctl.state === 'armed') wrapTrace();
                ctl.wantTimer = global.setTimeout(wantPoll, 250);
            }
        };
        ctl.wantTimer = global.setTimeout(wantPoll, 250);

        const layer = () => {
            const c = d.createElement('canvas');
            c.width = stage.Wd; c.height = stage.Hd;
            const x = c.getContext('2d');
            x.setTransform(stage.DPR, 0, 0, stage.DPR, 0, 0);
            return [c, x];
        };
        const D0 = (t) => tree.Dmax * 0.9 * (1 - Math.exp(-Math.max(0, t - GROW_AT_S) / 2.4));

        async function start() {
            if (ctl.state !== 'armed') return;
            ctl.state = 'starting';
            try {
                const host = options.host;
                if (!work || !host || host.hidden) throw new Error('no picker');
                const rect = work.getBoundingClientRect();
                if (rect.width < 120 || rect.height < 120) throw new Error('no stage');
                const DPR = Math.min(global.devicePixelRatio || 1, 2);
                const Wd = Math.round(rect.width * DPR), Hd = Math.round(rect.height * DPR);
                const bg = backgroundOf(host);
                stage = { rect, DPR, Wd, Hd, W: Wd / DPR, H: Hd / DPR, bg, bgL: lum(bg) };
                canvas = d.createElement('canvas');
                canvas.className = 'memora-neural-reveal-layer';
                canvas.setAttribute('aria-hidden', 'true');
                canvas.width = Wd; canvas.height = Hd;
                canvas.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;width:${rect.width}px;height:${rect.height}px;z-index:2147483500;pointer-events:auto;opacity:0;`;
                ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('no canvas');
                const ink = parseColor(getComputedStyle(host).color) || [236, 235, 228, 1];
                const dark = stage.bgL < 0.45;
                pal = {
                    ink: (dark ? [231, 221, 203] : ink.slice(0, 3)).map(Math.round).join(','),
                    pulse: (dark ? [255, 246, 232] : ink.slice(0, 3)).map(Math.round).join(','),
                    accent: accentColor().slice(0, 3).map(Math.round).join(','),
                    bg: `rgb(${bg.slice(0, 3).map(Math.round).join(',')})`
                };
                /* The chosen lectures' thumbnails are the pieces of the picker that stay. With
                   several lectures selected the arbor grows out of each one that is on screen. */
                const rows = [...new Set((options.origins?.() || [options.origin?.()]).filter(Boolean))];
                const found = rows.map(sourceOf).filter(Boolean).slice(0, 6);
                const picker = await pixels(host, stage);
                if (ctl.state !== 'starting') return;
                thumbs = found.length ? found : [{ box: { x: stage.W * 0.08, y: stage.H * 0.3, w: 54, h: 36 }, rad: 6, art: null }];
                const ix = picker.I.getContext('2d');
                for (const t of found) {
                    const { box, rad } = t;
                    const [c, x] = layer();
                    x.beginPath(); x.roundRect(box.x, box.y, box.w, box.h, rad); x.clip();
                    x.drawImage(picker.S, 0, 0, stage.W, stage.H); x.drawImage(picker.I, 0, 0, stage.W, stage.H);
                    t.art = c;
                    ix.save(); ix.setTransform(DPR, 0, 0, DPR, 0, 0); ix.globalCompositeOperation = 'destination-out';
                    ix.beginPath(); ix.roundRect(box.x - 0.5, box.y - 0.5, box.w + 1, box.h + 1, rad); ix.fill(); ix.restore();
                }
                pick = { S: picker.S, I: picker.I };
                tree = buildTree(stage, thumbs.map((t) => t.box));
                d.body.appendChild(canvas);
                ctl.state = 'cloud';
                ctl.t0 = performance.now();
                frame(ctl.t0);
            } catch (error) {
                if (ctl.state === 'starting') console.info('Neural reveal skipped:', error?.message || error);
                dispose();
                ctl.state = 'failed';
            }
        }

        /* Terminals leave from the nearest visible point of the arbor, at least a short reach away,
           and each contact inks its part of the surface. */
        function prepareLanding(card, sampled, targets, tM) {
            const [pIn, pinCtx] = layer(), [pOut, poutCtx] = layer();
            const mask = (x, src, op) => { x.drawImage(src, 0, 0, stage.W, stage.H); x.globalCompositeOperation = op; x.drawImage(sampled.S, 0, 0, stage.W, stage.H); x.globalCompositeOperation = 'source-over'; };
            mask(pinCtx, pick.S, 'destination-in');
            mask(poutCtx, pick.S, 'destination-out');
            const Dv = D0(tM), r = rng(77), gap = Math.min(0.08, 1.2 / Math.max(1, targets.length));
            const terms = targets.map((el, i) => {
                const A = [el.kind === 'text' ? el.x - 5 : el.x, el.y + el.h / 2];
                let best = null, bd = 1e9;
                for (const P of tree.net) {
                    for (let j = 0; j < P.x.length; j += 2) {
                        if (P.d[j] > Dv - 12) break;
                        const dd = Math.hypot(P.x[j] - A[0], P.y[j] - A[1]);
                        if (dd >= 28 && dd < bd && P.k[j] > 0.2) { bd = dd; best = [P.x[j], P.y[j], P.d[j], P.w[j]]; }
                    }
                }
                if (!best) { const b = thumbs[i % thumbs.length].box; best = [b.x + b.w / 2, b.y + b.h / 2, 0, 1]; }
                const net = [];
                trunk(net, r, [best[0], best[1]], A, { bend: (r() - 0.5) * 0.3, w0: cl(best[3] * 0.8, 0.7, 1.1), w1: 0.5, alpha: 0.95, seed: 900 + i * 13, lag: best[2], root: null, wobble: 3 });
                const P = finalize(net)[0], len = P.dEnd - P.d[0], ti = tM + gap * i;
                return { P, el, A, ti, ci: ti + 0.25 + Math.min(len, 900) / 600, loop: el.kind === 'box' ? rrLoop(el.x, el.y, el.w, el.h, el.rad) : null };
            });
            const full = (el) => (el.kind === 'text' ? [el.x - 1, el.y - 2, el.w + 2, el.h + 4] : [el.x - 1, el.y - 1, el.w + 2, el.h + 2]);
            const cL = terms.length ? Math.max(...terms.map((m) => m.ci)) : tM + 0.3;
            // Where the arbor clears out, and each terminal's lane: the stage minus every other part.
            const holes = new Path2D();
            targets.forEach((el) => holes.roundRect(el.x - 6, el.y - 6, el.w + 12, el.h + 12, 8));
            terms.forEach((m) => {
                m.lane = new Path2D();
                m.lane.rect(-10, -10, stage.W + 20, stage.H + 20);
                terms.forEach((o) => { if (o !== m) m.lane.rect(o.el.x - 2, o.el.y - 2, o.el.w + 4, o.el.h + 4); });
            });
            return { card, S: sampled.S, I: sampled.I, pIn, pOut, terms, full, holes, tM, c0: terms.length ? terms[0].ci : tM + 0.3, cL, rest: null };
        }

        function draw(tau) {
            const { W, H, DPR } = stage, L = landing;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.globalAlpha = 1;
            ctx.fillStyle = pal.bg;
            ctx.fillRect(0, 0, stage.Wd, stage.Hd);
            ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
            const img = (c, a) => { if (a <= 0.003) return; ctx.globalAlpha = Math.min(1, a); ctx.drawImage(c, 0, 0, W, H); ctx.globalAlpha = 1; };
            // Surfaces never dim: the picker's own fill hands over to the module's under the arbor.
            if (!L) img(pick.S, 1);
            else {
                const sm = easeIO(ramp(tau, L.tM, L.tM + 0.8));
                img(L.pOut, 1 - sm);
                img(L.pIn, 1 - ss(L.tM + 0.8, L.tM + 1.4, tau));
                img(L.S, sm);
            }
            img(pick.I, 1 - ss(0.2, 0.9, tau));
            const tM = L ? L.tM : Infinity;
            const D = tau < tM ? D0(tau) : lerp(D0(tM), tree.Dmax, easeIO(ramp(tau, tM, tM + 1.4)));
            /* The arbor never lies over the content. Once the module's surface is known it clears out
               of every part a terminal will ink, so the ink lands on clean surface, and from the first
               contact it lets go from the thumbnail end, finishing as the last contacts land. */
            const dim = L ? 1 - 0.45 * ss(L.c0 - 0.1, L.c0 + 0.6, tau) : 1;
            const Dr = L ? (tree.Dmax + 220) * easeIO(ramp(tau, L.c0, L.cL + 0.6)) : 0;
            const fall = (dist) => (Dr > 0 ? ss(Dr, Dr + 70, dist) : 1);
            if (!net) net = layer();
            const [netCanvas, nx] = net;
            nx.save(); nx.setTransform(1, 0, 0, 1, 0, 0); nx.clearRect(0, 0, stage.Wd, stage.Hd); nx.restore();
            if (!L || Dr < tree.Dmax + 220) drawNet(nx, tree.net, () => D, (dist) => dim * fall(dist), pal.ink);
            const until = Math.min(tau, tM), first = Math.max(0, Math.floor((until - GROW_AT_S - 0.4 - (tree.Dmax + 60) / 380) / 0.42));
            for (let k = first; ; k += 1) {
                const sk = GROW_AT_S + 0.4 + k * 0.42;
                if (sk >= until) break;
                const P = tree.trunks[(k * 3) % tree.trunks.length], s = (tau - sk) * 380;
                if (s < Math.min(D, P.dEnd) + 56) drawSignal(nx, P, Math.min(s, D), pal.pulse);
            }
            const clear = L ? ss(L.tM, L.tM + 0.45, tau) : 0;
            if (clear > 0) {
                nx.save();
                nx.globalCompositeOperation = 'destination-out';
                nx.globalAlpha = clear;
                nx.filter = `blur(${(5 * DPR).toFixed(1)}px)`;
                nx.fill(L.holes);
                nx.restore();
            }
            ctx.drawImage(netCanvas, 0, 0, W, H);
            if (L) {
                const la = 1 - ss(L.cL + 0.4, L.cL + 0.8, tau), wipes = new Path2D(), contacts = [];
                let any = false;
                ctx.strokeStyle = `rgba(${pal.ink},${(0.5 * la).toFixed(3)})`;
                for (const m of L.terms) {
                    const { P, el, ti, ci } = m;
                    // A terminal touches its part, then pulls back tip first; it never crosses another part.
                    const reach = easeIO(ramp(tau, ti, ci)) - easeIO(ramp(tau, ci + 0.12, ci + 0.6));
                    if (tau > ti && reach > 0.002) {
                        ctx.save();
                        ctx.clip(m.lane, 'evenodd');
                        drawNet(ctx, [P], () => P.d[0] + (P.dEnd - P.d[0]) * reach, () => 1, pal.ink);
                        ctx.restore();
                    }
                    if (tau <= ci) continue;
                    const f = L.full(el);
                    if (el.kind === 'text') wipes.rect(f[0], f[1], f[2] * easeOut(ramp(tau, ci, ci + 0.5)), f[3]);
                    else {
                        // The traced outline hands over to the part's own border as its fill lands.
                        const ta = 0.5 * la * (1 - ss(ci + 0.45, ci + 0.85, tau));
                        if (ta > 0.01) {
                            ctx.strokeStyle = `rgba(${pal.ink},${ta.toFixed(3)})`;
                            ctx.lineWidth = 1; ctx.lineCap = 'round';
                            const half = m.loop.total / 2 * easeOut(ramp(tau, ci, ci + 0.5));
                            strokeSpan(ctx, m.loop, m.loop.start - half, m.loop.start + half);
                        }
                        wipes.rect(f[0], f[1], f[2] * easeOut(ramp(tau, ci + 0.2, ci + 0.7)), f[3]);
                    }
                    any = true;
                    contacts.push(m);
                }
                if (any) { ctx.save(); ctx.clip(wipes); img(L.I, 1); ctx.restore(); }
                // Ink outside every target joins once the last wipe is complete, drawn once only.
                const rest = ss(L.cL + 0.7, L.cL + 1.15, tau);
                if (rest > 0) {
                    if (!L.rest) {
                        const [c, x] = layer();
                        x.drawImage(L.I, 0, 0, W, H);
                        x.globalCompositeOperation = 'destination-out';
                        for (const m of L.terms) { const f = L.full(m.el); x.fillRect(f[0], f[1], f[2], f[3]); }
                        L.rest = c;
                    }
                    img(L.rest, rest);
                }
                for (const m of contacts) drawContact(ctx, m.A[0], m.A[1], (tau - m.ci) * 2, pal.accent);
            }
            // At landing each thumbnail contracts into a cell body, which fades as the first contact lands.
            const tf = L ? ss(tM - 0.1, tM + 0.5, tau) : 0;
            const sf = L ? tf * (1 - ss(L.c0 + 0.2, L.c0 + 0.55, tau)) : 0;
            for (const { box, rad, art } of thumbs) {
                const cx = box.x + box.w / 2, cy = box.y + box.h / 2;
                if (sf > 0.01) {
                    ctx.fillStyle = `rgba(${pal.ink},${(0.85 * sf).toFixed(3)})`;
                    ctx.beginPath(); ctx.ellipse(cx, cy, 7.5, 5, -0.25, 0, Math.PI * 2); ctx.fill();
                }
                if (!art || tf >= 1) continue;
                const s = 1 - 0.35 * tf;
                ctx.save();
                ctx.translate(cx, cy); ctx.scale(s, s); ctx.translate(-cx, -cy);
                ctx.globalAlpha = (1 - tf) * (1 - tf);
                ctx.drawImage(art, 0, 0, W, H);
                ctx.strokeStyle = `rgba(${pal.accent},${(0.35 * ss(0, 0.4, tau)).toFixed(3)})`;
                ctx.lineWidth = 1;
                ctx.beginPath(); ctx.roundRect(box.x - 0.5, box.y - 0.5, box.w + 1, box.h + 1, rad + 0.5); ctx.stroke();
                ctx.restore();
            }
            ctx.globalAlpha = 1;
        }

        function frame(now) {
            if (ctl.state !== 'cloud' && ctl.state !== 'landing') return;
            const tau = (now - ctl.t0) / 1000;
            ctl.tau = tau;
            if (ctl.state === 'cloud' && !ctl.landRequested && tau > MAX_CLOUD_S) { abort(); return; }
            if (ctl.state === 'landing' && landing.card && (!landing.card.isConnected || !landing.card.getClientRects().length)) { abort(); return; }
            const r = work.getBoundingClientRect();
            if (Math.abs(r.width - stage.rect.width) > 1 || Math.abs(r.height - stage.rect.height) > 1) { abort(); return; }
            draw(tau);
            canvas.style.opacity = String(1 - ss(ctl.T - 0.32, ctl.T - 0.02, tau));
            if (tau >= ctl.T) { finish(); return; }
            ctl.raf = global.requestAnimationFrame(frame);
        }

        /* The surface is ready to sample once its box holds still and it has finished fading in:
           modules open with an opacity transition, and sampling mid-fade captures a blank card. */
        const shown = (el) => {
            let alpha = 1;
            for (let node = el; node && node.nodeType === 1; node = node.parentElement) alpha *= Number(getComputedStyle(node).opacity) || 0;
            // Finite entrance transitions inside the surface count as still fading; spinners do not.
            const busy = el.getAnimations?.({ subtree: true }).some((animation) => animation.playState === 'running'
                && Number.isFinite(animation.effect?.getComputedTiming?.().endTime));
            return busy ? 0 : alpha;
        };
        function settle(getCard) {
            return new Promise((resolve) => {
                const began = performance.now();
                let last = '', stable = 0;
                const check = () => {
                    if (ctl.state !== 'cloud') { resolve(null); return; }
                    const card = getCard();
                    const r = card?.isConnected ? card.getBoundingClientRect() : null;
                    const key = r && r.width > 40 && r.height > 40 ? [r.left, r.top, r.width, r.height].map(Math.round).join() : '';
                    stable = key && key === last && shown(card) > 0.98 ? stable + 1 : 0;
                    last = key;
                    if (stable >= 4) { resolve(card); return; }
                    if (performance.now() - began > 2500) { resolve(key ? card : null); return; }
                    global.requestAnimationFrame(check);
                };
                global.requestAnimationFrame(check);
            });
        }

        async function land(getCard) {
            if (ctl.state === 'armed') await start();
            for (let i = 0; ctl.state === 'starting' && i < 100; i += 1) await new Promise((r) => global.setTimeout(r, 20));
            if (ctl.state !== 'cloud') return;
            ctl.landRequested = true;
            ctl.committed = true;
            const card = await settle(getCard);
            if (!card || ctl.state !== 'cloud') { abort(); return; }
            try {
                if (d.fonts?.ready) await d.fonts.ready;
                // The whole work area when it holds the surface, so chrome around the card (the
                // quiz header, a paired pane) is already on the last frame and nothing pops in.
                const root = work.contains(card) ? work : card;
                const sampled = await pixels(root, stage);
                if (ctl.state !== 'cloud') return;
                const targets = collectTargets(root, stage);
                landing = prepareLanding(card, sampled, targets, Math.max(ctl.tau + 0.05, MIN_READY_S));
                // The canvas hands off only after the arbor has let go and the last ink has settled.
                ctl.T = landing.cL + 1.6;
                ctl.state = 'landing';
            } catch (error) {
                console.warn('Neural reveal could not sample the module surface:', error);
                abort();
                return;
            }
            await new Promise((resolve) => { ctl.landResolve = resolve; });
        }

        function dispose() {
            global.cancelAnimationFrame(ctl.raf);
            global.clearTimeout(ctl.wantTimer);
            canvas?.remove();
            canvas = null; ctx = null; landing = null; net = null;
            if (current === ctl) current = null;
            ctl.landResolve?.();
            ctl.landResolve = null;
        }

        function finish() {
            ctl.state = 'done';
            dispose();
        }

        function abort() {
            if (ctl.state === 'done' || ctl.state === 'aborted' || ctl.state === 'failed') return;
            const shown = canvas?.isConnected;
            ctl.state = 'aborted';
            global.cancelAnimationFrame(ctl.raf);
            global.clearTimeout(ctl.wantTimer);
            if (!shown) { dispose(); return; }
            canvas.style.transition = 'opacity 200ms ease';
            canvas.style.pointerEvents = 'none';
            canvas.style.opacity = '0';
            global.setTimeout(dispose, 220);
        }

        return ctl;
    }

    function arm(options = {}) {
        if (global.MemoraMotion?.isReduced?.()) return null;
        if (global.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return null;
        current?.abort();
        current = createController(options);
        wrapTrace();
        return current;
    }

    global.MemoraNeuralReveal = Object.freeze({
        version: 2,
        arm,
        get active() { return current ? current.state : ''; }
    });
})(window);
